import { LivekitBaseController } from "./livekit_base_controller"

export default class extends LivekitBaseController {
  static targets = ["button", "stopButton", "statusText"]
  static values = {
    conversationId: Number,
    language: String,
    livekitWsUrl: String
  }

  connect() {
    this.isRecording = false
    this.isShuttingDown = false
    this.shutdownTimeout = 3000
    this.shutdownTimer = null
    this.room = null
    this.committedText = ""
    this.partialText = ""
    this.inputField = document.getElementById("tutor_message_input")
  }

  disconnect() {
    this.stopRecording()
  }

  async toggleRecording() {
    if (this.isRecording) {
      this.handleStopRecording()
    } else {
      await this.handleStartRecording()
    }
  }

  async handleStartRecording() {
    try {
      this.committedText = ""
      this.partialText = ""
      if (this.inputField) this.inputField.value = ""

      this.updateUI("connecting")

      const tokenData = await this.fetchLiveKitToken({
        mode: "stt_only",
        language: this.languageValue || "en"
      })

      await this.connectToRoom(tokenData, {
        onConnected: () => this.updateUI("connected"),
        onDisconnected: () => this.updateUI("disconnected"),
        onReconnecting: () => this.updateUI("connecting"),
        onDataReceived: this.handleDataReceived.bind(this)
      })

      await this.publishMicrophone()
      this.setRecordingState(true)
    } catch (error) {
      this.handleRecordingError(error)
    }
  }

  handleStopRecording() {
    if (!this.isRecording || this.isShuttingDown) return

    this.isShuttingDown = true
    this.updateUI("processing")
    this.stopPublishing()
    this.sendEndSignal()
    this.waitForFinalTranscripts()
  }

  handleDataReceived(payload, participant, kind, topic) {
    if (topic !== "stt") return

    try {
      const text = new TextDecoder().decode(payload)
      const msg = JSON.parse(text)

      if (msg.type === "partial") {
        this.partialText = msg.text || ""
        this.renderInput()
      } else if (msg.type === "final") {
        this.committedText += (this.committedText ? " " : "") + (msg.text || "")
        this.partialText = ""
        this.renderInput()
      } else if (msg.type === "done") {
        if (this.isShuttingDown) this.completeShutdown()
      }
    } catch (e) {
      console.error("[TutorSTT] Bad stt payload", e)
    }
  }

  sendEndSignal() {
    if (!this.room) return
    try {
      const encoder = new TextEncoder()
      const data = encoder.encode(JSON.stringify({ type: "client_stopping" }))
      this.room.localParticipant.publishData(data, { reliable: true, topic: "control" })
    } catch (error) {
      console.error('[TutorSTT] Failed to send end signal:', error)
    }
  }

  waitForFinalTranscripts() {
    this.shutdownTimer = setTimeout(() => {
      this.completeShutdown()
    }, this.shutdownTimeout)
  }

  completeShutdown() {
    if (this.shutdownTimer) {
      clearTimeout(this.shutdownTimer)
      this.shutdownTimer = null
    }
    this.isShuttingDown = false
    this.disconnectFromRoom()
    this.setRecordingState(false)
  }

  renderInput() {
    if (!this.inputField) return
    const combined = `${this.committedText}${this.partialText}`
    this.inputField.value = combined
    this.inputField.selectionStart = this.inputField.selectionEnd = combined.length
  }

  setRecordingState(isRecording) {
    this.isRecording = isRecording
    this.updateUI(isRecording ? "recording" : "idle")
  }

  updateUI(status) {
    if (!this.hasButtonTarget) return

    const uiStates = {
      'idle': () => {
        this.showElement(this.buttonTarget)
        this.hideElement(this.stopButtonTarget)
        this.hideStatusText()
      },
      'connecting': () => {
        this.hideElement(this.buttonTarget)
        this.showElement(this.stopButtonTarget)
        this.showStatusText("Connecting...")
      },
      'connected': () => {
        this.showStatusText("Connected - Speak to dictate")
      },
      'recording': () => {
        this.hideElement(this.buttonTarget)
        this.showElement(this.stopButtonTarget)
        this.showStatusText("Listening...")
      },
      'processing': () => {
        this.hideElement(this.buttonTarget)
        this.showElement(this.stopButtonTarget)
        this.showStatusText("Processing final words...")
      },
      'disconnected': () => {
        this.showElement(this.buttonTarget)
        this.hideElement(this.stopButtonTarget)
        this.hideStatusText()
      }
    }

    const handler = uiStates[status]
    if (handler) handler()
  }

  showElement(target) {
    if (target) target.classList.remove('hidden')
  }

  hideElement(target) {
    if (target) target.classList.add('hidden')
  }

  showStatusText(message) {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = message
      this.statusTextTarget.classList.remove('hidden')
    }
  }

  hideStatusText() {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.classList.add('hidden')
    }
  }

  handleRecordingError(error) {
    console.error("Tutor STT recording error:", error)
    this.showStatusText(`Error: ${error.message}`)
    this.setRecordingState(false)
  }

  generateRoomName() {
    return `tutor-stt-${this.conversationIdValue}-${Date.now()}`
  }

  generateParticipantName() {
    return `user-${this.conversationIdValue}`
  }

  startRecording() {
    return this.handleStartRecording()
  }

  stopRecording() {
    return this.handleStopRecording()
  }
}
